/**
 * SyncActivityLog Component
 *
 * Displays sync activity for a project with a "Sync Now" button
 * and a table of recent sync log entries. Placed in the Monitor tab
 * of ProjectReport.
 */

import React, { useState, useMemo } from 'react';
import {
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  MessageSquare,
  Hash,
  Key,
  Ticket,
  Download,
  Users,
  ShieldAlert,
  User as UserIcon,
  Webhook,
  Clock,
  Plus,
  Minus,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { EmptyState } from '@/components/EmptyState';
import { toast } from '@/lib/toast';
import { cn, formatTimestamp, formatRelativeTime } from '@/lib/utils';
import { useSyncLogs, useTriggerSync } from '@/hooks/useSync';
import { useTesters } from '@/hooks/useTesters';
import type { SyncLogEntry, SyncLogResult } from '@/types/domain';

interface SyncActivityLogProps {
  projectId: string;
}

type OutcomeFilter = 'all' | 'changes' | 'no-changes' | 'errors';
type SourceFilter = 'all' | 'manual' | 'graph_webhook' | 'polling_fallback';

const sourceConfig: Record<string, {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
}> = {
  manual:           { label: 'Manual',  icon: UserIcon, iconClass: 'text-primary' },
  graph_webhook:    { label: 'Webhook', icon: Webhook,  iconClass: 'text-info' },
  polling_fallback: { label: 'Polling', icon: Clock,    iconClass: 'text-muted-foreground' },
};

const fieldIcons: Record<string, React.ReactNode> = {
  status: <FileText className="h-3.5 w-3.5" />,
  actual_result: <CheckCircle2 className="h-3.5 w-3.5" />,
  comments: <MessageSquare className="h-3.5 w-3.5" />,
  transaction: <Hash className="h-3.5 w-3.5" />,
  key_fields: <Key className="h-3.5 w-3.5" />,
  issue_nr: <Ticket className="h-3.5 w-3.5" />,
  assigned_user: <Users className="h-3.5 w-3.5" />,
};

const fieldLabels: Record<string, string> = {
  status: 'Status',
  actual_result: 'Actual Result',
  comments: 'Comments',
  transaction: 'Transaction',
  key_fields: 'Key Fields',
  issue_nr: 'Issue Number',
  excel_status: 'Excel Status',
  assigned_user: 'Reassigned',
};

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ValueChip({ value }: { value: string }) {
  if (!value || !value.trim()) {
    return <span className="text-muted-foreground italic">empty</span>;
  }
  if (UUID_RE.test(value.trim())) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-mono tabular-nums text-foreground cursor-help">
              {value.slice(0, 8)}…
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <span className="font-mono">{value}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (value.length > 60) {
    return (
      <span className="truncate text-foreground" title={value}>
        {value}
      </span>
    );
  }
  return <span className="text-foreground">{value}</span>;
}

function CascadePill({
  created = 0,
  deleted = 0,
  updated = 0,
  showZero = false,
  unit = 'TC',
}: {
  created?: number;
  deleted?: number;
  updated?: number;
  showZero?: boolean;
  unit?: string;
}) {
  if (!created && !deleted && !updated) {
    return showZero ? <span className="text-muted-foreground/50">—</span> : null;
  }
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {created > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium tabular-nums bg-success/10 text-success">
          <Plus className="h-2.5 w-2.5" />{created} {unit}
        </span>
      )}
      {updated > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium tabular-nums bg-info/10 text-info">
          {updated} {unit}
        </span>
      )}
      {deleted > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium tabular-nums bg-destructive/10 text-destructive">
          <Minus className="h-2.5 w-2.5" />{deleted} {unit}
        </span>
      )}
    </div>
  );
}

function TriggerCell({ source }: { source: string }) {
  const cfg = sourceConfig[source] ?? sourceConfig.manual;
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <Icon className={cn('h-3.5 w-3.5 shrink-0', cfg.iconClass)} />
      <span>{cfg.label}</span>
    </div>
  );
}

function classifyOutcome(entry: SyncLogEntry): 'error' | 'mismatch' | 'changes' | 'noop' {
  if (entry.result.error_type === 'structural_mismatch') return 'mismatch';
  if (entry.result.error_count > 0) return 'error';
  if (entry.result.has_changes) return 'changes';
  return 'noop';
}

function outcomeBorderClass(kind: ReturnType<typeof classifyOutcome>): string {
  switch (kind) {
    case 'error':
    case 'mismatch':
      return 'border-l-destructive';
    case 'changes':
      return 'border-l-info';
    case 'noop':
      return 'border-l-muted-foreground/30';
  }
}

function getDiffCounts(diffs: NonNullable<SyncLogResult['diffs']>): Map<string, number> {
  const counts = new Map<string, number>();
  for (const d of diffs) {
    const key = d.field === 'excel_status' ? 'status' : d.field;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

function OutcomeCell({ entry }: { entry: SyncLogEntry }) {
  const isMappingSync = entry.sync_type === 'mapping';
  const kind = classifyOutcome(entry);
  const r = entry.result;

  if (isMappingSync) {
    const total = (r.mappings_created ?? 0) + (r.mappings_updated ?? 0) + (r.mappings_deleted ?? 0);
    if (total === 0) {
      return (
        <span className="text-sm text-muted-foreground flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-success/50" />
          No mapping changes
        </span>
      );
    }
    return (
      <div className="space-y-1">
        <div className="text-sm flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-info" />
          <span><span className="font-semibold tabular-nums">{total}</span> mapping{total !== 1 ? 's' : ''}</span>
        </div>
        <CascadePill
          created={r.mappings_created ?? 0}
          updated={r.mappings_updated ?? 0}
          deleted={r.mappings_deleted ?? 0}
          unit="map"
        />
      </div>
    );
  }

  if (kind === 'mismatch') {
    return (
      <div className="text-destructive flex items-center gap-1.5 text-sm">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
        <span>Structure mismatch ({r.structural_details?.mismatch_percentage ?? '?'}%)</span>
      </div>
    );
  }

  if (kind === 'error') {
    return (
      <div className="space-y-1">
        <div className="text-destructive flex items-center gap-1.5 text-sm">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span><span className="tabular-nums">{r.error_count}</span> error{r.error_count !== 1 ? 's' : ''}</span>
        </div>
        {r.has_changes && (
          <span className="text-xs text-muted-foreground">
            + <span className="tabular-nums">{r.steps_updated}</span> step{r.steps_updated !== 1 ? 's' : ''} updated
          </span>
        )}
      </div>
    );
  }

  if (kind === 'changes') {
    const diffCounts = r.diffs ? getDiffCounts(r.diffs) : new Map();
    const tcCreated = r.test_cases_created ?? 0;
    const tcDeleted = r.test_cases_deleted ?? 0;
    const tcUpdated = r.test_cases_updated ?? 0;
    return (
      <div className="space-y-1">
        <div className="text-sm flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-info" />
          <span>
            <span className="font-semibold tabular-nums">{r.steps_updated}</span>
            {' '}of <span className="tabular-nums">{r.steps_checked}</span> step{r.steps_updated !== 1 ? 's' : ''} updated
          </span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {Array.from(diffCounts.entries()).map(([field, count]) => (
            <span
              key={field}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium tabular-nums bg-muted text-muted-foreground"
            >
              <span className="text-foreground/70">{fieldIcons[field]}</span>
              {count} {fieldLabels[field] ?? field}
            </span>
          ))}
          <CascadePill created={tcCreated} updated={tcUpdated} deleted={tcDeleted} />
        </div>
      </div>
    );
  }

  return (
    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
      <CheckCircle2 className="h-3.5 w-3.5 text-success/50" />
      No changes
      {r.steps_checked > 0 && (
        <span className="text-xs tabular-nums">({r.steps_checked} checked)</span>
      )}
    </span>
  );
}

type DiffItem = NonNullable<SyncLogResult['diffs']>[0];

function DiffLine({
  diff,
  userLookup,
}: {
  diff: DiffItem;
  userLookup?: Map<string, string>;
}) {
  const isReassignment = diff.field === 'assigned_user';
  const resolveUser = (val: string): string => {
    const trimmed = val?.trim();
    if (!trimmed || !userLookup || !UUID_RE.test(trimmed)) return val;
    return userLookup.get(trimmed) ?? val;
  };
  const fromVal = isReassignment ? resolveUser(diff.from) : diff.from;
  const toVal = isReassignment ? resolveUser(diff.to) : diff.to;

  return (
    <div className="flex items-baseline gap-2 text-sm min-w-0">
      <Badge variant="outline" className="text-[10px] font-mono tabular-nums shrink-0 px-1.5 py-0 h-5">
        Step {diff.step_index}
      </Badge>
      <span className="min-w-0 truncate"><ValueChip value={fromVal} /></span>
      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
      <span className="min-w-0 truncate font-medium"><ValueChip value={toVal} /></span>
    </div>
  );
}

function DiffsByFieldType({
  diffs,
  userLookup,
}: {
  diffs: NonNullable<SyncLogResult['diffs']>;
  userLookup?: Map<string, string>;
}) {
  const grouped = useMemo(() => {
    return diffs.reduce((acc, diff) => {
      if (!acc[diff.field]) acc[diff.field] = [];
      acc[diff.field].push(diff);
      return acc;
    }, {} as Record<string, DiffItem[]>);
  }, [diffs]);

  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([field, fieldDiffs]) => (
        <div key={field} className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <span className="text-foreground/70">{fieldIcons[field] ?? <FileText className="h-3.5 w-3.5" />}</span>
            {fieldLabels[field] ?? field}
            <span className="tabular-nums text-muted-foreground/70">({fieldDiffs.length})</span>
          </div>
          <div className="ml-5 space-y-1">
            {fieldDiffs.map((diff, i) => (
              <DiffLine key={i} diff={diff} userLookup={userLookup} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function StructuralMismatchDetails({
  details,
  projectId,
  fallbackMessage,
}: {
  details?: SyncLogResult['structural_details'];
  projectId: string;
  fallbackMessage?: string;
}) {
  const triggerSync = useTriggerSync(projectId);

  const handleForceSync = (e: React.MouseEvent) => {
    e.stopPropagation();
    const promise = triggerSync.mutateAsync({ force: true });
    toast.promise(promise, {
      loading: 'Force syncing from SharePoint...',
      success: 'Force sync complete — structural check was skipped',
      error: (err) => `Force sync failed: ${err.message}`,
    });
  };

  const hasCounts =
    !!details && (details.total_compared > 0 || details.mismatch_count > 0);
  const sampleMismatches = details?.sample_mismatches ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 p-3 bg-destructive/5 border border-destructive/20 rounded-md">
        <ShieldAlert className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
        <div className="space-y-1 text-sm">
          <p className="font-medium text-destructive">
            Structure Mismatch{hasCounts && (
              <> (<span className="tabular-nums">{details!.mismatch_percentage}%</span>)</>
            )}
          </p>
          <p className="text-muted-foreground">
            {hasCounts ? (
              <>
                The Excel file content appears to have been replaced with a different test scenario.
                <span className="tabular-nums"> {details!.mismatch_count}</span> of
                <span className="tabular-nums"> {details!.total_compared}</span> steps have mismatched descriptions.
              </>
            ) : (
              fallbackMessage ?? 'Step descriptions diverged between SharePoint and the stored scenario.'
            )}
          </p>
        </div>
      </div>

      {sampleMismatches.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Step comparison (top {sampleMismatches.length})
          </p>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground w-16">Step</th>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Excel</th>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">DynamoDB</th>
                  <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground w-16">Match</th>
                </tr>
              </thead>
              <tbody>
                {sampleMismatches.map((m) => (
                  <tr key={m.step_index} className="border-t">
                    <td className="px-3 py-1.5 font-mono text-xs tabular-nums">{m.step_index}</td>
                    <td className="px-3 py-1.5 text-xs truncate max-w-[200px]" title={m.excel}>{m.excel}</td>
                    <td className="px-3 py-1.5 text-xs truncate max-w-[200px]" title={m.dynamo}>{m.dynamo}</td>
                    <td className="px-3 py-1.5 text-xs text-right text-destructive font-mono tabular-nums">
                      {Math.round(m.ratio * 100)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        <Button
          variant="destructive"
          size="sm"
          onClick={handleForceSync}
          disabled={triggerSync.isPending}
          className="gap-2"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', triggerSync.isPending && 'animate-spin')} />
          {triggerSync.isPending ? 'Force Syncing...' : 'Force Sync'}
        </Button>
        <span className="text-xs text-muted-foreground">
          Applies changes despite mismatched structure
        </span>
      </div>
    </div>
  );
}

const SyncLogRow = React.memo(({
  entry,
  projectId,
  userLookup,
}: {
  entry: SyncLogEntry;
  projectId: string;
  userLookup?: Map<string, string>;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isMappingSync = entry.sync_type === 'mapping';
  // Accept both error types: 'structural_mismatch' is the first-detection log;
  // 'structural_mismatch_skipped' was emitted by the pre-fix backend on retries
  // (kept here so historical SYNC_LOG entries still render the Force Sync button).
  const isStructuralError =
    entry.result.error_type === 'structural_mismatch' ||
    entry.result.error_type === 'structural_mismatch_skipped';
  const hasDiffs = (entry.result.diffs?.length ?? 0) > 0;
  const hasErrorDetails = (entry.result.errors?.length ?? 0) > 0;
  const mappingTotal = isMappingSync
    ? (entry.result.mappings_created ?? 0) + (entry.result.mappings_updated ?? 0) + (entry.result.mappings_deleted ?? 0)
    : 0;
  const hasMappingDetails = isMappingSync && (mappingTotal > 0 || !!entry.file_name || hasErrorDetails);
  const isExpandable = isMappingSync
    ? hasMappingDetails
    : (hasDiffs || hasErrorDetails || isStructuralError || entry.result.steps_checked > 0);
  const kind = classifyOutcome(entry);
  const isError = kind === 'error' || kind === 'mismatch';

  return (
    <>
      <TableRow
        className={cn(
          'border-l-2',
          outcomeBorderClass(kind),
          isExpandable && 'cursor-pointer hover:bg-accent/5',
          isError && 'bg-destructive/5',
        )}
        onClick={isExpandable ? () => setExpanded(!expanded) : undefined}
        role={isExpandable ? 'button' : undefined}
        tabIndex={isExpandable ? 0 : undefined}
        aria-expanded={isExpandable ? expanded : undefined}
        onKeyDown={isExpandable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        } : undefined}
      >
        <TableCell className="align-middle">
          <div className="flex items-center gap-1.5">
            {isExpandable ? (
              expanded
                ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
            ) : (
              <span className="w-3 shrink-0" />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="leading-tight">
                    <div className="text-sm tabular-nums">{formatTimestamp(entry.timestamp)}</div>
                    <div className="text-[11px] text-muted-foreground tabular-nums flex items-center gap-1">
                      <span>{formatRelativeTime(entry.timestamp)}</span>
                      <span className="opacity-50">·</span>
                      <span>{formatDuration(entry.duration_ms)}</span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div>{new Date(entry.timestamp).toLocaleString()}</div>
                  <div className="text-xs opacity-70">Duration: {formatDuration(entry.duration_ms)}</div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </TableCell>
        <TableCell className="align-middle">
          <TriggerCell source={entry.source} />
        </TableCell>
        <TableCell className="align-middle">
          {isMappingSync ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium">Mapping sync</span>
              {entry.file_name && (
                <span className="text-xs text-muted-foreground truncate" title={entry.file_name}>
                  {entry.file_name}
                </span>
              )}
            </div>
          ) : (
            <div className="text-sm font-medium truncate" title={entry.scenario_name || entry.scenario_id}>
              {entry.scenario_name || (
                <span className="font-mono tabular-nums">{entry.scenario_id.slice(0, 8)}…</span>
              )}
            </div>
          )}
        </TableCell>
        <TableCell className="align-middle">
          <OutcomeCell entry={entry} />
        </TableCell>
      </TableRow>
      {expanded && isExpandable && (
        <tr>
          <td colSpan={4} className="p-0">
            <div className={cn(
              'px-6 py-4 border-t border-l-2 space-y-4',
              outcomeBorderClass(kind),
              isError ? 'bg-destructive/5' : 'bg-muted/30',
            )}>
              {isStructuralError && (
                <StructuralMismatchDetails
                  details={entry.result.structural_details}
                  projectId={projectId}
                  fallbackMessage={entry.result.errors?.[0]}
                />
              )}
              {isMappingSync && (
                <div className="space-y-3">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Mapping sync details
                  </div>
                  <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-1.5 text-sm">
                    {entry.file_name && (
                      <>
                        <dt className="text-muted-foreground">Source file</dt>
                        <dd className="font-mono text-xs break-all">{entry.file_name}</dd>
                      </>
                    )}
                    <dt className="text-muted-foreground">Trigger</dt>
                    <dd>
                      <TriggerCell source={entry.source} />
                    </dd>
                    <dt className="text-muted-foreground">Duration</dt>
                    <dd className="tabular-nums">{formatDuration(entry.duration_ms)}</dd>
                    <dt className="text-muted-foreground">Mappings</dt>
                    <dd>
                      {mappingTotal === 0 ? (
                        <span className="text-muted-foreground">No changes</span>
                      ) : (
                        <CascadePill
                          created={entry.result.mappings_created ?? 0}
                          updated={entry.result.mappings_updated ?? 0}
                          deleted={entry.result.mappings_deleted ?? 0}
                          unit="map"
                        />
                      )}
                    </dd>
                  </dl>
                  {mappingTotal > 0 && (
                    <p className="text-xs text-muted-foreground border-t border-border/50 pt-2">
                      Per-mapping diffs (which user, area, subdomain changed) are recorded in the
                      <span className="font-medium text-foreground"> Mapping Activity</span> card.
                    </p>
                  )}
                </div>
              )}
              {hasDiffs && (
                <div className="space-y-3">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Changes detected
                  </div>
                  <DiffsByFieldType diffs={entry.result.diffs!} userLookup={userLookup} />
                  <div className="pt-2 border-t border-border/50 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span>
                      <span className="tabular-nums">{entry.result.steps_updated}</span> of
                      <span className="tabular-nums"> {entry.result.steps_checked}</span> steps updated
                    </span>
                    <CascadePill
                      created={entry.result.test_cases_created ?? 0}
                      updated={entry.result.test_cases_updated ?? 0}
                      deleted={entry.result.test_cases_deleted ?? 0}
                    />
                  </div>
                </div>
              )}
              {hasErrorDetails && !isStructuralError && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-destructive uppercase tracking-wide">
                    Sync errors
                  </div>
                  {entry.result.errors!.map((err, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <AlertCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                      <p className="text-sm text-destructive">{err}</p>
                    </div>
                  ))}
                </div>
              )}
              {!hasDiffs && !hasErrorDetails && !isStructuralError && entry.result.steps_checked > 0 && (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <span>
                    Checked <span className="tabular-nums">{entry.result.steps_checked}</span> step{entry.result.steps_checked !== 1 ? 's' : ''}
                    {' '}in &quot;{entry.scenario_name || entry.scenario_id}&quot; — no changes detected
                  </span>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
});

SyncLogRow.displayName = 'SyncLogRow';

export function SyncActivityLog({ projectId }: SyncActivityLogProps) {
  const { data, isLoading } = useSyncLogs(projectId);
  const triggerSync = useTriggerSync(projectId);
  const { data: testersData } = useTesters();
  const [showAll, setShowAll] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

  const userLookup = useMemo(() => {
    const m = new Map<string, string>();
    testersData?.testers?.forEach(t => {
      m.set(t.id, t.name || t.email || t.id);
    });
    return m;
  }, [testersData]);

  const handleSync = () => {
    const promise = triggerSync.mutateAsync(undefined);
    toast.promise(promise, {
      loading: 'Syncing from SharePoint...',
      success: (result) => {
        setSyncSuccess(true);
        setTimeout(() => setSyncSuccess(false), 3000);
        const { summary } = result;
        const message = summary.total_steps_updated > 0
          ? `Sync complete: ${summary.total_steps_updated} steps updated across ${summary.scenarios_synced} scenarios`
          : `Sync complete: no changes detected in ${summary.scenarios_synced} scenarios`;

        const announcement = document.createElement('div');
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.className = 'sr-only';
        announcement.textContent = message;
        document.body.appendChild(announcement);
        setTimeout(() => document.body.removeChild(announcement), 1000);

        return message;
      },
      error: (err) => `Sync failed: ${err.message}`,
    });
  };

  const logs = useMemo(() => data?.logs ?? [], [data?.logs]);

  const outcomeCounts = useMemo(() => {
    const counts: Record<OutcomeFilter, number> = {
      all: logs.length,
      changes: 0,
      'no-changes': 0,
      errors: 0,
    };
    for (const log of logs) {
      if (log.result.error_count > 0 || log.result.error_type === 'structural_mismatch') {
        counts.errors++;
      }
      if (log.result.has_changes) counts.changes++;
      else counts['no-changes']++;
    }
    return counts;
  }, [logs]);

  const sourceCounts = useMemo(() => {
    const counts: Record<SourceFilter, number> = {
      all: logs.length,
      manual: 0,
      graph_webhook: 0,
      polling_fallback: 0,
    };
    for (const log of logs) {
      if (log.source in counts) counts[log.source as SourceFilter]++;
    }
    return counts;
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      if (outcomeFilter === 'changes' && !log.result.has_changes) return false;
      if (outcomeFilter === 'no-changes' && log.result.has_changes) return false;
      if (outcomeFilter === 'errors' && log.result.error_count === 0 && log.result.error_type !== 'structural_mismatch') return false;
      if (sourceFilter !== 'all' && log.source !== sourceFilter) return false;
      return true;
    });
  }, [logs, outcomeFilter, sourceFilter]);

  const handleExport = () => {
    const csv = [
      'Timestamp,Source,Sync Type,Scenario,File,Steps Checked,Steps Updated,Has Changes,Errors,TC Created,TC Updated,TC Deleted,Mappings Created,Mappings Updated,Mappings Deleted,Duration (ms)',
      ...filteredLogs.map(log => [
        new Date(log.timestamp).toISOString(),
        log.source,
        log.sync_type ?? 'scenario',
        log.scenario_name || log.scenario_id,
        log.file_name,
        log.result.steps_checked,
        log.result.steps_updated,
        log.result.has_changes,
        log.result.error_count,
        log.result.test_cases_created ?? 0,
        log.result.test_cases_updated ?? 0,
        log.result.test_cases_deleted ?? 0,
        log.result.mappings_created ?? 0,
        log.result.mappings_updated ?? 0,
        log.result.mappings_deleted ?? 0,
        log.duration_ms,
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sync-activity-${projectId}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Sync activity exported to CSV');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sync Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0 && !triggerSync.isPending) {
    return (
      <EmptyState
        icon={RefreshCw}
        title="No Sync Activity"
        description="Trigger a manual sync to check for changes in SharePoint Excel files."
        action={{
          label: 'Sync Now',
          onClick: handleSync,
        }}
      />
    );
  }

  const outcomeFilters: { key: OutcomeFilter; label: string; count: number; activeClass: string }[] = [
    { key: 'all',         label: 'All',         count: outcomeCounts.all,         activeClass: 'bg-foreground text-background border-foreground' },
    { key: 'changes',     label: 'Changes',     count: outcomeCounts.changes,     activeClass: 'bg-info/10 text-info border-info/30' },
    { key: 'no-changes',  label: 'No changes',  count: outcomeCounts['no-changes'], activeClass: 'bg-muted text-muted-foreground border-border' },
    { key: 'errors',      label: 'Errors',      count: outcomeCounts.errors,      activeClass: 'bg-destructive/10 text-destructive border-destructive/30' },
  ];

  const sourceFilters: { key: SourceFilter; label: string; count: number }[] = [
    { key: 'all',              label: 'All triggers', count: sourceCounts.all },
    { key: 'manual',           label: 'Manual',       count: sourceCounts.manual },
    { key: 'graph_webhook',    label: 'Webhook',      count: sourceCounts.graph_webhook },
    { key: 'polling_fallback', label: 'Polling',      count: sourceCounts.polling_fallback },
  ];

  const displayLogs = showAll ? filteredLogs : filteredLogs.slice(0, 5);
  const filtersActive = outcomeFilter !== 'all' || sourceFilter !== 'all';

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-primary" />
              Sync Activity
            </CardTitle>
            <CardDescription>
              {logs.length} sync event{logs.length !== 1 ? 's' : ''}
              {filteredLogs.length !== logs.length && (
                <> · showing {filteredLogs.length}</>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExport}
              disabled={filteredLogs.length === 0}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={triggerSync.isPending}
              className="gap-2"
            >
              {syncSuccess ? (
                <CheckCircle2 className="h-4 w-4 text-success" />
              ) : (
                <RefreshCw className={cn(
                  'h-4 w-4',
                  triggerSync.isPending && 'animate-spin',
                )} />
              )}
              {triggerSync.isPending ? 'Syncing...' : syncSuccess ? 'Done' : 'Sync Now'}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70 mr-1">Outcome</span>
            {outcomeFilters.filter(f => f.key === 'all' || f.count > 0).map(f => {
              const isActive = outcomeFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => {
                    setOutcomeFilter(f.key);
                    setShowAll(false);
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border transition-colors',
                    isActive
                      ? f.activeClass
                      : 'bg-transparent text-muted-foreground border-border hover:bg-muted',
                  )}
                >
                  <span>{f.label}</span>
                  <span className={cn('tabular-nums', isActive ? 'opacity-90' : 'opacity-60')}>
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="h-4 w-px bg-border" />

          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70 mr-1">Trigger</span>
            {sourceFilters.filter(f => f.key === 'all' || f.count > 0).map(f => {
              const isActive = sourceFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => {
                    setSourceFilter(f.key);
                    setShowAll(false);
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border transition-colors',
                    isActive
                      ? 'bg-foreground text-background border-foreground'
                      : 'bg-transparent text-muted-foreground border-border hover:bg-muted',
                  )}
                >
                  <span>{f.label}</span>
                  <span className={cn('tabular-nums', isActive ? 'opacity-90' : 'opacity-60')}>
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[150px] pl-3">Time</TableHead>
              <TableHead className="w-[110px]">Trigger</TableHead>
              <TableHead className="w-[280px]">Scenario</TableHead>
              <TableHead>Outcome</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayLogs.map((entry, idx) => (
              <SyncLogRow
                key={`${entry.timestamp}-${idx}`}
                entry={entry}
                projectId={projectId}
                userLookup={userLookup}
              />
            ))}
          </TableBody>
        </Table>
        {filteredLogs.length === 0 && filtersActive && (
          <div className="py-8 text-center space-y-2">
            <p className="text-sm text-muted-foreground">No sync activity matches the active filters.</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOutcomeFilter('all');
                setSourceFilter('all');
              }}
            >
              Clear filters
            </Button>
          </div>
        )}
        {filteredLogs.length > 5 && (
          <div className="mt-3 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll(!showAll)}
              className="text-muted-foreground"
            >
              {showAll ? 'Show less' : `Show all ${filteredLogs.length} entries`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
